'use client'
import { getAmplitudeSession } from '@/features/shared/analytics/integrations'
import { isLocal } from '@/features/shared/utils/environment'
import { scripts } from '@/lib/ThirdPartyScripts/scripts'
import { useConsentManager } from '@/lib/transcend/useConsentManager'
import { getAnonymousId } from '@shipt/analytics-member-web'
import Script from 'next/script'
import { useIsCurrentRoutePCI } from '@/features/shared/hooks/routes/useIsCurrentRoutePCI'

const gtmContainerUrl = process.env.NEXT_PUBLIC_GTM_CONTAINER_URL
const amplitudeSessionReplayApiKey = process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_API_KEY

interface AmplitudeSessionReplay {
  init(
    apiKey: string,
    options: {
      deviceId: string
      sessionId: number
      sampleRate?: number
      privacyConfig?: { maskSelector?: string[] }
      useWebWorker?: boolean
    },
  ): void
}

declare global {
  interface Window {
    sessionReplay?: AmplitudeSessionReplay
  }
}

// nextjs app router automatically generates <link rel="preload"> hints for scripts
// with the default "afterInteractive" strategy. this happens even with conditional rendering
// since nextjs statically analyzes <Script> components and generates preload hints regardless
// of runtime conditions. this conflicts with Transcend Airgap's request interception, causing
// "invalid href value" warnings. using "lazyOnload" skips preload generation, allowing Airgap
// to properly gate the request.
const SCRIPT_LOAD_STRATEGY = 'lazyOnload'

/**
 * Contains script tags for non-pci-approved 3rd party scripts. This should only be
 * loaded on non-PCI pages.
 */
function NonPCIApprovedScripts() {
  const elementIdPrefix = 'shipt-script'
  return (
    <>
      <Script
        type="text/javascript"
        id={`${elementIdPrefix}-mouseflow`}
        dangerouslySetInnerHTML={{
          __html: scripts.mouseflow,
        }}
      />
      {!!amplitudeSessionReplayApiKey && (
        <Script
          src="https://cdn.amplitude.com/libs/session-replay-browser-1.43.0-min.js.gz"
          type="text/javascript"
          id={`${elementIdPrefix}-amplitude-session-replay`}
          strategy={SCRIPT_LOAD_STRATEGY}
          onLoad={() => {
            const deviceId = getAnonymousId()
            if (!deviceId) return

            window.sessionReplay?.init(amplitudeSessionReplayApiKey, {
              deviceId,
              sessionId: getAmplitudeSession(),
              privacyConfig: {
                maskSelector: ['[data-amp-mask]'],
              },
              useWebWorker: true,
            })
          }}
        />
      )}
      {!isLocal && (
        <>
          <Script
            type="text/javascript"
            id={`${elementIdPrefix}-branch`}
            dangerouslySetInnerHTML={{
              __html: scripts.branch,
            }}
          />
          <Script
            type="text/javascript"
            src="https://resources.digital-cloud-west.medallia.com/wdcwest/3490/onsite/embed.js"
            async
            strategy={SCRIPT_LOAD_STRATEGY}
          />
        </>
      )}
      {Boolean(gtmContainerUrl) && (
        <Script
          id={`${elementIdPrefix}-gtm-bootstrapper`}
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: scripts.googletagmanager,
          }}
        />
      )}
    </>
  )
}
export function ThirdPartyScripts() {
  const isPCI = useIsCurrentRoutePCI()
  const { airgap } = useConsentManager()
  const loadThirdPartyScripts = airgap && !isPCI

  return (
    <>
      {/* The scripts below are not approved for the PCI environment, and should only be loaded on non-pci pages */}
      {!!loadThirdPartyScripts && <NonPCIApprovedScripts />}
    </>
  )
}
